import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { endpoints, downloadRunPdf } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sparkles,
  Wrench,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Brain,
  Play,
  Radio,
  FileDown,
} from "lucide-react";

const SUGGESTED = [
  "Create a private offer for opportunity SF-OPP-8842 with a 3-year commit through Presidio.",
  "Draft a CPPO for Nordic Freight AB (SF-OPP-8901) — first-time AWS Marketplace buyer.",
  "Renew Meridian Health (SF-OPP-9013) via an Agreement-Based Offer through AHEAD.",
];

function iconFor(kind) {
  switch (kind) {
    case "thought": return Brain;
    case "tool_call": return Wrench;
    case "tool_result": return CheckCircle2;
    case "message": return MessageSquare;
    case "text_delta": return Radio;
    case "approval_required": return AlertTriangle;
    case "error": return XCircle;
    case "done": return Sparkles;
    default: return MessageSquare;
  }
}

function colorFor(kind) {
  switch (kind) {
    case "thought": return "text-amber-600 border-amber-500/30";
    case "tool_call": return "text-sky-700 border-sky-500/30";
    case "tool_result": return "text-emerald-700 border-emerald-500/30";
    case "message": return "text-zinc-800 border-zinc-300";
    case "text_delta": return "text-zinc-600 border-zinc-200";
    case "approval_required": return "text-orange-600 border-orange-500/50";
    case "error": return "text-rose-600 border-rose-500/40";
    case "done": return "text-emerald-700 border-emerald-500/40";
    default: return "text-zinc-700 border-zinc-300";
  }
}

export default function AgentConsole() {
  const [intent, setIntent] = useState(SUGGESTED[0]);
  const [runs, setRuns] = useState([]);
  const [active, setActive] = useState(null);      // full run record (metadata)
  const [events, setEvents] = useState([]);        // live event stream
  const [streaming, setStreaming] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [exporting, setExporting] = useState(false);
  const esRef = useRef(null);
  const scrollRef = useRef(null);
  const streamBufferRef = useRef("");
  const [searchParams] = useSearchParams();
  const deepLinkRun = searchParams.get("run");

  const loadRuns = () => endpoints.runs().then(setRuns);

  useEffect(() => {
    loadRuns();
    return () => esRef.current?.close();
  }, []);

  useEffect(() => {
    if (!deepLinkRun) return;
    endpoints.getRun(deepLinkRun).then((r) => {
      setActive(r);
      if (r.status === "running") openStream(r.id);
      else { esRef.current?.close(); setEvents(r.events || []); setStreaming(false); }
    }).catch(() => toast.error("Run not found in this workspace"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkRun]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events.length]);

  const openStream = (runId) => {
    esRef.current?.close();
    setEvents([]);
    streamBufferRef.current = "";
    setStreaming(true);

    const es = new EventSource(endpoints.streamRunUrl(runId));
    esRef.current = es;

    es.onmessage = (msg) => {
      let ev;
      try {
        ev = JSON.parse(msg.data);
      } catch {
        return;
      }
      if (ev.kind === "__end__") {
        es.close();
        setStreaming(false);
        // refresh run metadata (status, result_authorization_id)
        endpoints.getRun(runId).then((r) => setActive(r));
        loadRuns();
        return;
      }
      // Merge consecutive text_delta events into the last event for a smooth typing feel
      if (ev.kind === "text_delta") {
        streamBufferRef.current += ev.label || "";
        setEvents((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.kind === "text_delta") {
            const updated = [...prev.slice(0, -1), { ...last, label: streamBufferRef.current }];
            return updated;
          }
          streamBufferRef.current = ev.label || "";
          return [...prev, { ...ev, label: streamBufferRef.current }];
        });
        return;
      }
      // Non-delta events: reset the delta buffer for the next stream cycle
      streamBufferRef.current = "";
      setEvents((prev) => [...prev, ev]);

      if (ev.kind === "approval_required") {
        endpoints.getRun(runId).then((r) => setActive(r));
      }
    };

    es.onerror = () => {
      es.close();
      setStreaming(false);
    };
  };

  const startRun = async () => {
    if (!intent.trim()) return;
    const r = await endpoints.startRun(intent);
    setActive(r);
    openStream(r.id);
    loadRuns();
    toast.success("Agent run started", { description: r.id });
  };

  const selectExisting = async (r) => {
    esRef.current?.close();
    setActive(r);
    setEvents(r.events || []);
    setStreaming(false);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const seal = await downloadRunPdf(active.id);
      toast.success("Signed trace PDF exported", { description: `seal ${seal}` });
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const approve = async () => {
    if (confirmText.trim() !== "PUBLISH") {
      toast.error("Type PUBLISH exactly to confirm the irreversible action.");
      return;
    }
    const r = await endpoints.approveRun(active.id);
    toast.success(
      r.submission_mode === "live" ? "Authorization submitted to AWS Marketplace Catalog" : "Authorization published (SIMULATED) & CPPO released",
      { description: `${r.ammp_authorization_id} · ${r.offer_id}` }
    );
    setApproveOpen(false);
    setConfirmText("");
    const refreshed = await endpoints.getRun(active.id);
    setActive(refreshed);
    setEvents(refreshed.events || []);
    loadRuns();
  };

  const reject = async () => {
    await endpoints.rejectRun(active.id);
    toast("Run cancelled", { description: "Draft was not published." });
    const refreshed = await endpoints.getRun(active.id);
    setActive(refreshed);
    setEvents(refreshed.events || []);
    loadRuns();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] h-full min-h-0" data-testid="page-agent">
      <div className="border-r border-zinc-200 flex flex-col h-full min-h-0">
        <div className="p-6 border-b border-zinc-200">
          <div className="tx-overline mb-2">New agent run</div>
          <h2 className="font-heading font-black text-2xl mb-4">State your intent.</h2>
          <Textarea
            data-testid="agent-intent-input"
            value={intent}
            onChange={(e) => setIntent(e.target.value)}
            placeholder="e.g. Create a private offer for SF-OPP-8842…"
            className="min-h-[100px] bg-white border-zinc-200 font-mono text-[13px] resize-none focus-visible:ring-orange-500/40"
          />
          <div className="mt-3 space-y-1.5">
            {SUGGESTED.map((s) => (
              <button
                key={s}
                data-testid="suggested-intent"
                onClick={() => setIntent(s)}
                className="w-full text-left text-[11px] text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 px-2 py-1.5 rounded-sm border border-transparent hover:border-zinc-300 transition-colors leading-snug"
              >
                → {s}
              </button>
            ))}
          </div>
          <Button
            onClick={startRun}
            data-testid="start-run-button"
            className="w-full mt-4 h-10 bg-zinc-900 text-white hover:bg-zinc-700 rounded-sm gap-2"
          >
            <Play className="w-4 h-4" />
            Dispatch agent
          </Button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-6 py-3 tx-overline sticky top-0 bg-white border-b border-zinc-200">
            Run history
          </div>
          <div className="divide-y divide-zinc-200">
            {runs.map((r) => (
              <button
                key={r.id}
                data-testid="run-item"
                onClick={() => selectExisting(r)}
                className={`w-full text-left px-6 py-3.5 hover:bg-zinc-100 transition-colors ${active?.id === r.id ? "bg-zinc-50" : ""}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[10px] text-zinc-500">{r.id}</span>
                  <StateBadge state={r.status} />
                </div>
                <div className="text-[12px] text-zinc-700 line-clamp-2">{r.intent}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col h-full min-h-0 bg-zinc-50 tx-scanlines">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <div className="w-14 h-14 border border-zinc-200 rounded-sm flex items-center justify-center mb-6">
              <Brain className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
            </div>
            <div className="tx-overline mb-3">Agent console · SSE streaming</div>
            <p className="text-zinc-500 max-w-sm text-[13px] leading-relaxed">
              Dispatch a run to watch tokens stream live over Server-Sent Events —
              every reasoning delta, tool call and result, and the drafted
              authorization awaiting your approval.
            </p>
          </div>
        ) : (
          <>
            <header className="h-14 px-6 border-b border-zinc-200 flex items-center justify-between shrink-0 bg-white">
              <div>
                <div className="font-mono text-[11px] text-zinc-500 flex items-center gap-2">
                  {active.id}
                  {streaming && (
                    <span className="flex items-center gap-1 text-orange-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500 tx-pulse-dot" />
                      streaming
                    </span>
                  )}
                </div>
                <div className="text-[13px] text-zinc-800 line-clamp-1 max-w-xl">{active.intent}</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={exportPdf}
                  disabled={exporting || streaming}
                  data-testid="export-trace-pdf-button"
                  title="Export signed PDF audit record"
                  className="h-8 px-3 border border-zinc-300 hover:border-orange-500/60 hover:text-orange-600 text-[11px] font-mono flex items-center gap-2 whitespace-nowrap transition-colors disabled:opacity-40"
                >
                  <FileDown className="w-3.5 h-3.5" /> {exporting ? "sealing…" : "Signed PDF"}
                </button>
                <StateBadge state={active.status} />
              </div>
            </header>

            <div
              ref={scrollRef}
              data-testid="agent-trace"
              className="flex-1 overflow-y-auto px-6 py-6 font-mono text-[12px] leading-relaxed"
            >
              {events.map((ev, i) => {
                const Icon = iconFor(ev.kind);
                return (
                  <div
                    key={i}
                    data-testid={`trace-${ev.kind}`}
                    className={`flex gap-3 mb-3 pl-3 border-l ${colorFor(ev.kind)} tx-fade-in`}
                  >
                    <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.8} />
                    <div className="flex-1 min-w-0">
                      <div className="uppercase tracking-widest text-[10px] text-zinc-500 mb-0.5">
                        {ev.kind}
                      </div>
                      <div className="whitespace-pre-wrap break-words">{ev.label}</div>
                      {ev.data && ev.kind !== "text_delta" && (
                        <pre className="mt-1.5 text-[11px] text-zinc-600 bg-white border border-zinc-200 p-2 rounded-sm overflow-x-auto">
                          {JSON.stringify(ev.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                );
              })}
              {streaming && (
                <div className="flex items-center gap-2 text-zinc-500 pl-3">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full tx-pulse-dot" />
                  agent thinking…
                </div>
              )}
            </div>

            {active.status === "awaiting_approval" && (
              <div className="border-t border-orange-500/40 bg-orange-50 p-5 shrink-0">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-heading font-medium text-orange-700 mb-1">
                      Irreversible gate — human approval required
                    </div>
                    <p className="text-[12px] text-zinc-600 mb-3 max-w-2xl">
                      The agent has drafted an AWS ISV Selling Authorization.
                      Publishing locks pricing, currency, duration and dimensions.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setApproveOpen(true)}
                        data-testid="open-approve-dialog"
                        className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm"
                      >
                        Publish & Release CPPO
                      </Button>
                      <Button
                        onClick={reject}
                        data-testid="reject-run"
                        variant="outline"
                        className="border-zinc-300 text-zinc-700 hover:bg-zinc-100 rounded-sm"
                      >
                        Reject draft
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent
          className="bg-white border border-orange-500/40 rounded-sm max-w-lg"
          data-testid="approve-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Publish AWS Selling Authorization
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              This action is <span className="text-orange-600">irreversible</span>.
              To continue, type <span className="font-mono text-orange-600">PUBLISH</span> below.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="publish-confirm-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type PUBLISH"
            className="bg-zinc-50 border-zinc-200 font-mono focus-visible:ring-orange-500/40"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setApproveOpen(false)}
              className="border-zinc-300 hover:bg-zinc-100 rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={approve}
              data-testid="confirm-publish-button"
              disabled={confirmText.trim() !== "PUBLISH"}
              className="bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white rounded-sm"
            >
              Publish & Release
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
